import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { shareUrl, receiptUrl, ogImageUrl, track } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Share2, Download } from "lucide-react";

export default function ShareSheet({ open, onOpenChange, shortId, predictionText, authorName }) {
  const url = shareUrl(shortId);
  const link = receiptUrl(shortId);
  const text = predictionText
    ? `${(authorName || "Someone")} is calling it: "${predictionText}"`
    : `${authorName || "Someone"}'s Call on the record`;

  const channels = [
    ["X", `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`],
    ["Threads", `https://www.threads.net/intent/post?text=${encodeURIComponent(text + " " + url)}`],
    ["Facebook", `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`],
    ["LinkedIn", `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`],
    ["Reddit", `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`],
    ["WhatsApp", `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`],
    ["Telegram", `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`],
    ["Email", `mailto:?subject=${encodeURIComponent("A Call on the record")}&body=${encodeURIComponent(text + "\n\n" + url)}`],
  ];

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    track("share_channel_selected", { channel: "copy", short_id: shortId });
    toast.success("Receipt link copied");
  };

  const native = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Called It", text, url });
        track("share_channel_selected", { channel: "native", short_id: shortId });
      } catch {}
    } else copy();
  };

  const openChannel = (name, href) => {
    track("share_channel_selected", { channel: name.toLowerCase(), short_id: shortId });
    window.open(href, "_blank", "noopener");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="card-brutal border-2 border-ink sm:max-w-md" data-testid="share-sheet">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">Share My Call</DialogTitle>
        </DialogHeader>
        <img src={ogImageUrl(shortId)} alt="Share card preview"
          className="w-full border-2 border-ink mb-1" loading="lazy" />
        <div className="flex gap-2">
          <button data-testid="share-copy-btn" onClick={copy}
            className="flex-1 h-11 border-2 border-ink bg-white font-semibold btn-press shadow-brutal-sm flex items-center justify-center gap-2">
            <Copy className="w-4 h-4" /> Copy link
          </button>
          <button data-testid="share-native-btn" onClick={native}
            className="flex-1 h-11 border-2 border-ink bg-brand text-white font-semibold btn-press shadow-brutal-sm flex items-center justify-center gap-2">
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>
        <div className="grid grid-cols-4 gap-2 pt-1">
          {channels.map(([name, href]) => (
            <button key={name} data-testid={`share-${name.toLowerCase()}`} onClick={() => openChannel(name, href)}
              className="h-11 border-2 border-ink bg-white text-xs font-bold btn-press shadow-brutal-sm hover:bg-locked">
              {name}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 pt-1">
          {["landscape", "square", "story"].map((s) => (
            <a key={s} data-testid={`download-${s}`} href={ogImageUrl(shortId, s)} target="_blank" rel="noopener"
              className="h-10 border-2 border-ink bg-secondary text-xs font-bold btn-press shadow-brutal-sm flex items-center justify-center gap-1 capitalize">
              <Download className="w-3 h-3" /> {s}
            </a>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
